// Google OAuth removido
import { jwtDecode } from 'jwt-decode';

// Types for decoded Google JWT
// GoogleProfile removido

export interface OnboardingUserData {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  picture: string;
  isEmailVerified: boolean;
  locale?: string;
}

export interface OnboardingTrialBootstrapState {
  email: string;
  shouldCreate: boolean;
  token?: string;
}

export const PAID_PLAN_TYPES = ['producer', 'professional'] as const;
export type PaidPlanType = (typeof PAID_PLAN_TYPES)[number];
export const DEFAULT_PAID_PLAN: PaidPlanType = 'producer';
export const PLAN_TYPES = ['free', ...PAID_PLAN_TYPES] as const;
export type PlanType = (typeof PLAN_TYPES)[number] | null;

export const normalizePlanType = (plan?: string | null): PlanType => {
  const normalized = String(plan || '').trim().toLowerCase();
  if (!normalized) return null;
  const mapped = normalized === 'premium' ? 'professional' : normalized;
  return (PLAN_TYPES as readonly string[]).includes(mapped) ? (mapped as PlanType) : null;
};

export const normalizePaidPlanType = (plan?: string | null): PaidPlanType | null => {
  const normalized = normalizePlanType(plan);
  if (!normalized) return null;
  return (PAID_PLAN_TYPES as readonly string[]).includes(normalized) ? (normalized as PaidPlanType) : null;
};

export const isPaidPlanType = (plan?: string | null): plan is PaidPlanType => {
  return Boolean(normalizePaidPlanType(plan));
};

export class OnboardingService {
  private readonly STORAGE_KEY = 'onboarding_data';
  private readonly PLAN_PARAM_KEY = 'plan';
  private readonly PLAN_SELECTION_KEY = 'selected_plan';
  private readonly PAYMENT_DATA_KEY = 'payment_data';
  private readonly ONBOARDING_TRIAL_BOOTSTRAP_KEY = 'onboarding_trial_bootstrap';

  // decodeGoogleCredential removido

  /**
   * Extracts plan parameter from current URL
   */
  getPlanFromUrl(): PlanType {
    const urlParams = new URLSearchParams(window.location.search);
    return normalizePlanType(urlParams.get('plan'));
  }

  /**
   * Store plan selection in session storage
   */
  storePlanSelection(plan: PlanType): void {
    try {
      if (plan) {
        sessionStorage.setItem(this.PLAN_SELECTION_KEY, plan);
      }
    } catch (error) {
      console.error('Error storing plan selection:', error);
    }
  }

  /**
   * Get stored plan selection
   */
  getStoredPlanSelection(): PlanType | null {
    try {
      return normalizePaidPlanType(sessionStorage.getItem(this.PLAN_SELECTION_KEY));
    } catch (error) {
      console.error('Error retrieving plan selection:', error);
      return null;
    }
  }

  /**
   * Store payment data in session storage
   */
  storePaymentData(data: PaymentData): void {
    try {
      sessionStorage.setItem(this.PAYMENT_DATA_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error storing payment data:', error);
    }
  }

  /**
   * Get stored payment data
   */
  getStoredPaymentData(): PaymentData | null {
    try {
      const stored = sessionStorage.getItem(this.PAYMENT_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error retrieving payment data:', error);
      return null;
    }
  }

  /**
   * Stores user data and plan for onboarding (no redirect)
   */
  async storeOnboardingData(userData: OnboardingUserData, plan: PlanType): Promise<void> {
    try {
      // Store user data temporarily in sessionStorage for the onboarding flow
      sessionStorage.setItem('onboarding_user', JSON.stringify(userData));
      const selectedPlan = normalizePaidPlanType(plan) || DEFAULT_PAID_PLAN;
      sessionStorage.setItem('selected_plan', selectedPlan);
    } catch (error) {
      console.error('Error storing onboarding data:', error);
      throw new Error('Failed to store onboarding data');
    }
  }

  // handleGoogleSignIn removido

  /**
   * Retrieves stored user data during onboarding
   */
  getOnboardingUserData(): OnboardingUserData | null {
    try {
      const userData = sessionStorage.getItem('onboarding_user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error retrieving onboarding user data:', error);
      return null;
    }
  }

  /**
   * Retrieves selected plan during onboarding
   */
  getSelectedPlan(): PlanType {
    try {
      const plan = normalizePaidPlanType(sessionStorage.getItem('selected_plan'));
      return plan || DEFAULT_PAID_PLAN;
    } catch (error) {
      console.error('Error retrieving selected plan:', error);
      return DEFAULT_PAID_PLAN;
    }
  }

  /**
   * Clears onboarding data from session storage
   */
  clearOnboardingData(): void {
    sessionStorage.removeItem('onboarding_user');
    sessionStorage.removeItem('selected_plan');
  }

  /**
   * Clear all stored data
   */
  clearAllStoredData(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.PLAN_SELECTION_KEY);
      sessionStorage.removeItem(this.PAYMENT_DATA_KEY);
      sessionStorage.removeItem(this.ONBOARDING_TRIAL_BOOTSTRAP_KEY);
    } catch (error) {
      console.error('Error clearing all stored data:', error);
    }
  }

  storeOnboardingTrialBootstrapState(state: OnboardingTrialBootstrapState): void {
    try {
      const normalizedEmail = String(state.email || '').trim().toLowerCase();
      if (!normalizedEmail) return;
      sessionStorage.setItem(
        this.ONBOARDING_TRIAL_BOOTSTRAP_KEY,
        JSON.stringify({
          email: normalizedEmail,
          shouldCreate: Boolean(state.shouldCreate),
          token: String(state.token || '').trim() || undefined,
        }),
      );
    } catch (error) {
      console.error('Error storing onboarding trial bootstrap state:', error);
    }
  }

  getOnboardingTrialBootstrapState(): OnboardingTrialBootstrapState | null {
    try {
      const raw = sessionStorage.getItem(this.ONBOARDING_TRIAL_BOOTSTRAP_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const email = String(parsed?.email || '').trim().toLowerCase();
      if (!email) return null;
      return {
        email,
        shouldCreate: Boolean(parsed?.shouldCreate),
        token: String(parsed?.token || '').trim() || undefined,
      };
    } catch (error) {
      console.error('Error retrieving onboarding trial bootstrap state:', error);
      return null;
    }
  }

  clearOnboardingTrialBootstrapState(): void {
    try {
      sessionStorage.removeItem(this.ONBOARDING_TRIAL_BOOTSTRAP_KEY);
    } catch (error) {
      console.error('Error clearing onboarding trial bootstrap state:', error);
    }
  }

  /**
   * Check if plan requires payment
   */
  isPaidPlan(plan: PlanType): boolean {
    return isPaidPlanType(plan);
  }

  /**
   * Get plan display name
   */
  getPlanDisplayName(plan: PlanType): string {
    if (!plan) return 'Plano Desconhecido';
    
    const names: Record<string, string> = {
      free: 'Plano Gratuito',
      producer: 'Plano Produtor',
      professional: 'Plano Profissional',
    };
    return names[plan] || 'Plano Desconhecido';
  }
}

// Payment data interface
export interface PaymentData {
  sessionId?: string;
  paymentIntentId?: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled';
  planType: PlanType;
  amount?: number;
  currency?: string;
  timestamp: string;
}

// Export singleton instance
export const onboardingService = new OnboardingService();
